import { Component, OnInit, signal, inject, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { TagModule } from 'primeng/tag';
import { TooltipModule } from 'primeng/tooltip';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Subject, takeUntil } from 'rxjs';

import { InboundService } from '../inbound.service';
import { WarehouseWarrantInfoVO } from '@app/api/model/warehouseWarrantInfoVO';
import { WarehouseWarrantDetailItem } from '../models/warehouse-warrant-detail-item';
import { TableColumn } from '@app/shared/components/data-table/data-table.component';
import { InboundFormWrapperComponent } from '../inbound-form-wrapper/inbound-form-wrapper.component';
import {
  getInboundStatusSeverity,
  getInboundStatusStyle,
  getInboundTypeLabel,
  getInboundStatusLabel,
} from '../inbound.util';

@Component({
  selector: 'app-inbound-warrant-view',
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    ButtonModule,
    TagModule,
    TooltipModule,
    InboundFormWrapperComponent,
  ],
  providers: [MessageService],
  templateUrl: './inbound-warrant-view.component.html',
  styleUrl: './inbound-warrant-view.component.css',
})
export class InboundWarrantViewComponent implements OnInit, OnDestroy {
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private messageService = inject(MessageService);
  private inboundService = inject(InboundService);
  private translateService = inject(TranslateService);
  private destroy$ = new Subject<void>();

  // Data
  inboundData = signal<WarehouseWarrantInfoVO | null>(null);
  itemDetails = signal<WarehouseWarrantDetailItem[]>([]);
  loading = signal(false);
  tabType = signal<'inbound-notice' | 'inbound-warrant'>('inbound-warrant');

  // Table columns for Item Details
  itemTableColumns: TableColumn[] = [
    {
      label: 'inbound.fields.sku_code',
      prop: 'skuCode',
      type: 'text',
      width: 150,
      align: 'left',
    },
    {
      label: 'inbound.fields.sku_name',
      prop: 'skuName',
      type: 'text',
      width: 200,
      align: 'left',
    },
    {
      label: 'inbound.fields.spu_code',
      prop: 'spuCode',
      type: 'text',
      width: 150,
      align: 'left',
    },
    {
      label: 'inbound.fields.spu_name',
      prop: 'spuName',
      type: 'text',
      width: 200,
      align: 'left',
    },
    {
      label: 'inbound.fields.planned_quantity',
      prop: 'plannedQty',
      type: 'text',
      width: 120,
      align: 'right',
    },
  ];

  ngOnInit(): void {
    const id = this.route.snapshot.paramMap.get('id');
    const tab = this.route.snapshot.queryParamMap.get('tab');

    // Set tab type based on query parameter
    if (tab === 'inbound-notice' || tab === 'inbound-warrant') {
      this.tabType.set(tab);
    }

    if (id) {
      this.loadInboundData(Number(id));
    } else {
      this.router.navigate(['/stock/inbound']);
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // Helper methods for displaying inbound type and status labels
  getInboundTypeLabel(inputType: string): string {
    return getInboundTypeLabel(inputType, this.translateService);
  }

  getInboundStatusLabel(status: string): string {
    return getInboundStatusLabel(status, this.translateService);
  }

  getStatusSeverity(
    status: string,
  ): 'success' | 'info' | 'warning' | 'danger' | 'secondary' {
    return getInboundStatusSeverity(status);
  }

  getStatusStyle(status: string): any {
    return getInboundStatusStyle(status);
  }

  formatCreationTime(dateString: string): string {
    if (!dateString) return '-';

    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return dateString; // Return original if invalid

      // Format as: "15/12/2023 14:30:00"
      return date.toLocaleDateString('en-GB', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false,
      });
    } catch (error) {
      return dateString; // Return original if formatting fails
    }
  }

  private loadInboundData(id: number): void {
    this.loading.set(true);

    this.inboundService
      .getWarehouseWarrantDetail(id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.inboundData.set(response);

          // Try different possible property names
          const itemDetailsData =
            response?.warehousewarrantDetailList ||
            response?.warehouseWarrantDetailList ||
            response?.warehouseWarrantDetails ||
            response?.itemDetails ||
            response?.items;

          if (
            itemDetailsData &&
            Array.isArray(itemDetailsData) &&
            itemDetailsData.length > 0
          ) {
            this.itemDetails.set(itemDetailsData);
          } else {
            this.itemDetails.set([]);
          }

          this.loading.set(false);
        },
        error: (error) => {
          this.loading.set(false);
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Failed to load inbound notice details',
          });
        },
      });
  }

  getFormattedDate(dateString: string): string {
    if (!dateString) return ' ';

    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return ' ';

      return date.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      });
    } catch (error) {
      return ' ';
    }
  }

  getFormattedTime(dateString: string): string {
    if (!dateString) return ' ';

    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return ' ';

      return date.toLocaleTimeString('en-GB', {
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false,
      });
    } catch (error) {
      return ' ';
    }
  }

  calculateTotalAmount(): number {
    const items = this.itemDetails();
    if (!items || items.length === 0) return 0;

    return items.reduce((total, item) => {
      const quantity = item.plannedQty || 0;
      const price = (item as any).price || 0;
      return total + quantity * price;
    }, 0);
  }

  onFormAction(event: { action: string; data?: any }): void {
    if (event.action === 'cancel') {
      this.onBack();
    } else if (event.action === 'go_back') {
      this.onBack();
    }
  }

  onTableAction(event: { section: string; action: string; data?: any }): void {
    // Handle table actions if needed
    console.log('Table action:', event);
  }

  onBack(): void {
    // Navigate back to the main inbound page
    // Check for tab query parameter first, then fallback to referrer logic
    const queryTab = this.route.snapshot.queryParams['tab'];
    let tab = 'inbound-notice'; // Default to inbound-notice tab

    if (queryTab) {
      tab = queryTab;
    } else {
      // Fallback to referrer logic if no tab parameter
      const referrer = document.referrer;
      if (referrer) {
        if (
          referrer.includes('/stock/inbound') &&
          referrer.includes('tab=inbound-warrant')
        ) {
          tab = 'inbound-warrant';
        } else if (referrer.includes('/edit') || referrer.includes('/add')) {
          tab = 'inbound-warrant';
        }
      }
    }

    this.router.navigate(['/stock/inbound'], {
      queryParams: { tab },
    });
  }
}
